import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { listStoryFilmExecutions, listStoryFilmAgentPreparations } from '../../api/story-film-prompt-packs.js'
import {
  annotateExecutionVersions,
  groupExecutionsByFilmAndEpisode,
  formatRelativeTime,
  formatExactTime,
} from '../../lib/story-film-time.js'

/**
 * 制作执行与样片准备任务列表管理
 */
export function useStoryFilmList() {
  const { t } = useI18n()

  const workspaceMode = ref('execution') // 'execution' | 'independent'
  const executionItems = ref([])
  const executionCursor = ref(null)
  const executionListLoading = ref(false)
  const executionListError = ref('')

  const preparations = ref([])
  const preparationCursor = ref(null)
  const preparationLoading = ref(false)
  const preparationError = ref('')

  const searchKeyword = ref('')
  const selectedFilm = ref('ALL')
  const selectedStatusFilter = ref('ALL') // 'ALL' | 'READY' | 'PROCESSING' | 'FAILED'
  const directInputId = ref('')
  const viewMode = ref('flat') // 'flat' | 'grouped'

  function recordErrorText(cause) {
    if (cause?.statusCode === 401) return t('storyFilm.executionLog.authRequired')
    if (cause?.statusCode === 403) return t('storyFilm.executionLog.recordForbidden')
    if (cause?.statusCode === 404) return t('storyFilm.executionLog.recordNotFound')
    return t('storyFilm.executionLog.recordLoadFailed')
  }

  async function loadExecutions(reset = true) {
    if (executionListLoading.value) return
    executionListLoading.value = true
    executionListError.value = ''
    try {
      const result = await listStoryFilmExecutions({ limit: 20, before_id: reset ? null : executionCursor.value })
      executionItems.value = reset ? result.items : [...executionItems.value, ...result.items]
      executionCursor.value = result.next_cursor
    } catch (cause) {
      executionListError.value = recordErrorText(cause)
    } finally {
      executionListLoading.value = false
    }
  }

  async function loadPreparations(reset = true) {
    if (preparationLoading.value) return
    preparationLoading.value = true
    preparationError.value = ''
    try {
      const result = await listStoryFilmAgentPreparations({ limit: 20, before_id: reset ? null : preparationCursor.value })
      preparations.value = reset ? result.items : [...preparations.value, ...result.items]
      preparationCursor.value = result.next_cursor
    } catch (cause) {
      preparationError.value = recordErrorText(cause)
    } finally {
      preparationLoading.value = false
    }
  }

  const currentFilterTargetList = computed(() => {
    return workspaceMode.value === 'execution' ? executionItems.value : preparations.value
  })

  const filmOptions = computed(() => {
    const map = new Map()
    for (const item of currentFilterTargetList.value) {
      const title = item.film_title || t('storyFilm.executionLog.unknown')
      map.set(title, (map.get(title) || 0) + 1)
    }
    return Array.from(map.entries()).map(([title, count]) => ({ title, count }))
  })

  const isFilterActive = computed(() => {
    return selectedFilm.value !== 'ALL' || selectedStatusFilter.value !== 'ALL' || searchKeyword.value.trim() !== ''
  })

  function resetFilters() {
    selectedFilm.value = 'ALL'
    selectedStatusFilter.value = 'ALL'
    searchKeyword.value = ''
  }

  const filteredExecutions = computed(() => {
    let list = [...executionItems.value]
    const q = searchKeyword.value.trim().toLowerCase()

    if (selectedFilm.value !== 'ALL') {
      list = list.filter(item => (item.film_title || t('storyFilm.executionLog.unknown')) === selectedFilm.value)
    }

    if (selectedStatusFilter.value === 'READY') {
      list = list.filter(item => item.status === 'media_ready' || item.status === 'ok')
    } else if (selectedStatusFilter.value === 'PROCESSING') {
      list = list.filter(item => ['queued', 'producing', 'running'].includes(item.status))
    } else if (selectedStatusFilter.value === 'FAILED') {
      list = list.filter(item => ['failed', 'rejected', 'error'].includes(item.status))
    }

    if (q) {
      list = list.filter(item => {
        const matchTitle = (item.film_title || '').toLowerCase().includes(q)
        const matchSubId = String(item.submission_id || '').includes(q)
        const matchTaskId = String(item.task_id || '').includes(q)
        const matchEpisode = `s${item.season_no}e${item.episode_no}`.includes(q)
        return matchTitle || matchSubId || matchTaskId || matchEpisode
      })
    }

    return annotateExecutionVersions(list)
  })

  const groupedExecutions = computed(() => {
    return groupExecutionsByFilmAndEpisode(filteredExecutions.value)
  })

  const filteredPreparations = computed(() => {
    let list = [...preparations.value]
    const q = searchKeyword.value.trim().toLowerCase()

    if (selectedFilm.value !== 'ALL') {
      list = list.filter(item => (item.film_title || t('storyFilm.executionLog.unknown')) === selectedFilm.value)
    }

    if (q) {
      list = list.filter(item => {
        const matchTitle = (item.film_title || '').toLowerCase().includes(q)
        const matchPrepId = String(item.preparation_id || '').includes(q)
        const matchScope = String(item.preparation_scope || '').toLowerCase().includes(q)
        return matchTitle || matchPrepId || matchScope
      })
    }

    return list
      .map(item => ({
        ...item,
        time_ago: formatRelativeTime(item.created_at),
        formatted_time: formatExactTime(item.created_at),
      }))
      .sort((a, b) => (Number(b.preparation_id) || 0) - (Number(a.preparation_id) || 0))
  })

  function switchWorkspace(mode) {
    workspaceMode.value = mode
    resetFilters()
  }

  return {
    workspaceMode,
    executionItems,
    executionCursor,
    executionListLoading,
    executionListError,
    preparations,
    preparationCursor,
    preparationLoading,
    preparationError,
    searchKeyword,
    selectedFilm,
    selectedStatusFilter,
    directInputId,
    viewMode,
    filmOptions,
    isFilterActive,
    filteredExecutions,
    groupedExecutions,
    filteredPreparations,
    loadExecutions,
    loadPreparations,
    resetFilters,
    switchWorkspace,
  }
}

